<template>
  <div>
    <vx-card class="w-full">
      <div class="flex justify-between items-center">
        <h4>{{ $t('정기위험성평가 엑셀 업로드') }}</h4>
        <vs-button color="secondary" @click="onClickSave">{{ $t('등록') }}</vs-button>
      </div>

      <!-- 기본정보 -->
      <field-select-module
        title="작성자 정보"
        v-model="form.stdInfo"
        :typeslabel="{
          date: '등록일',
        }"
        :board="board"
        :types="['department', 'com_name', 'rank', 'writer_name', 'date']"
      />

      <!-- 파일 업로드 -->
      <div>
        <div class="writeGridContainer">
          <div class="writeGridLineItem">
            <div>{{ $t('파일 업로드') }}</div>
            <div>
              <vs-button color="secondary" type="border" :href="excelLink">{{ $t('엑셀 템플릿 다운로드') }}</vs-button>
              <vs-button color="secondary" class="ml-2" @click="onClickExcelUpload">{{ $t('작성 엑셀 업로드') }}</vs-button>
              <input v-show="false" ref="excelInput" type="file" multiple @change="onSelectFile" accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" />
            </div>
          </div>
        </div>
      </div>

      <!-- 등록 대상 위험성평가 정보 -->
      <div>
        <div class="flex justify-between items-center mt-6 mb-4">
          <h5><i class="dot"></i>{{ $t('등록 대상 위험성평가 정보') }}</h5>
        </div>
        <div >
          <table>
            <colgroup>
              <col width="50" />
              <col width="60" />
              <col v-for="_ in 6" :key="`col_${_}`" />
              <col width="100" />
            </colgroup>
            <thead>
              <th></th>
              <th>No.</th>
              <th>{{ $t('사업장명') }}</th>
              <th>{{ $t('공종명') }}</th>
              <th>{{ $t('작업내용') }}</th>
              <th>{{ $t('평가일') }}</th>
              <th>{{ $t('실시기간(시작)') }}</th>
              <th>{{ $t('실시기간(종료)') }}</th>
              <th>{{ $t('수정/삭제') }}</th>
            </thead>
            <fragment v-for="(excel, excel_i) in excelData" :key="`위험성평가정보_${excel_i}`">
              <!-- 수정모드 -->
              <tbody v-if="excel_i === editExcelIndex">
                <tr>
                  <td class="cursor-pointer" @click="excel.open = !excel.open">
                    <img src="@/assets/icons/triangle_down.svg"
                         class="mx-auto duration-200"
                         :style="{ transform: excel.open ? 'rotate(0deg)' : 'rotate(-90deg)' }"
                    />
                  </td>
                  <td>{{ excel_i + 1 }}</td>
                  <td v-for="col in ['사업장명', '공종명', '작업내용', '평가일', '실시기간_시작', '실시기간_종료']" :key="`위험성평가정보_위험성평가_${col}`">
                    <div class="p-1">
                      <div class="flex items-center justify-center gap-2">
                        <field-selector v-if="col === '사업장명'" class="w-full" :field-name="editExcelData[col].field_name" @select="onSelectFieldSelector" />
                        <DateSelector v-else-if="['평가일', '실시기간_시작', '실시기간_종료'].indexOf(col) >= 0" v-model="editExcelData[col]" class="w-full" />
                        <ml-input v-else v-model="editExcelData[col]" class="w-full" />
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="flex justify-center gap-3">
                      <button @click="onClickFinishExcelData(excel, excel_i)">
                        <feather-icon icon="CheckIcon" svgClasses="h-5 w-5" />
                      </button>
                      <button @click="onClickCancelExcelData()">
                        <feather-icon icon="XIcon" svgClasses="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="excel.open">
                <td class="p-0 border-none" style="background: #fafafa;" colspan="9">
                  <!-- 위험성평가표 -->
                  <div class="p-4 pr-0 pb-0">
                    <div class="pl-2 mb-2 flex justify-between items-center ">
                      <h5 style="font-size: 14px;"><i class="dot" style="background: #2c2c2ccb"></i>{{ $t('위험성평가표') }}</h5>
                    </div>
                    <table class="sub-table border-bottom" style="background: #fff;">
                      <colgroup>
                        <col v-for="_ in 6" :key="`sub_col_1_${_}`" />
                        <col width="80" />
                        <col width="80" />
                        <col width="80" />
                        <col />
                        <col width="80" />
                        <col width="80" />
                        <col width="80" />
                        <col v-for="_ in 3" :key="`sub_col_3_${_}`" />
                      </colgroup>

                      <tr>
                        <th class="border-top" rowspan="2">{{ $t('세부작업명') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('평가구분') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('유해 위험요인') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('유해 위험요인 설명') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('재해형태') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('현재 안전보건조치') }}</th>
                        <th class="border-top" rowspan="1" colspan="3">{{ $t('현재 위험도') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('위험감소대책') }}</th>
                        <th class="border-top" rowspan="1" colspan="3">{{ $t('개선후 위험도') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('개선예정일') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('완료예정일') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('담당자') }}</th>
                      </tr>

                      <tr>
                        <th>{{ $t('가능성') }}</th>
                        <th>{{ $t('중대성') }}</th>
                        <th>{{ $t('위험도') }}</th>
                        <th>{{ $t('가능성') }}</th>
                        <th>{{ $t('중대성') }}</th>
                        <th>{{ $t('위험도') }}</th>
                      </tr>

                      <tr v-for="(위험성평가표, 위험성평가표_i) in editExcelData.위험성평가표" :key="`위험성평가표_edit_${위험성평가표_i}`">
                        <td class="p-1" v-for="col in [
                          '세부작업명', '평가구분', '유해위험요인', '유해위험요인설명', '재해형태', '현재안전보건조치',
                          '현재위험가능성', '현재위험중대성', '현재위험도', '위험감소대책', '개선위험가능성',
                          '개선위험중대성', '개선위험도', '개선예정일', '완료예정일', '담당자'
                          ]" :key="`위험성평가정보_위험성평가_${col}`">
                          <div class="p-1">
                            <div class="flex items-center justify-center gap-2">
                              <DateSelector v-if="['개선예정일', '완료예정일'].indexOf(col) >= 0"
                                            class="w-full"
                                            v-model="위험성평가표[col]"
                                            @change="(date) => {onChangeDate(date, 위험성평가표, col, editExcelData.errorMsg['위험성평가표'][위험성평가표_i])}"
                              />
                              <check-type-a-selector
                                v-else-if="['평가구분'].indexOf(col) >= 0"
                                v-model="위험성평가표[col]"
                                class="w-full"
                              />
                              <vs-select
                                v-else-if="['유해위험요인'].indexOf(col) >= 0"
                                class="w-full"
                                autocomplete
                                v-model="위험성평가표[col]"
                              >
                                <vs-select-item
                                  :key="index"
                                  :value="item.value"
                                  :text="item.text"
                                  v-for="(item, index) in handleAppraisalType(위험성평가표['평가구분'])"
                                />
                              </vs-select>
                              <disaster-class-selector
                                v-else-if="['재해형태'].indexOf(col) >= 0"
                                class="w-full"
                                v-model="위험성평가표[col]"
                                list-key="health"
                              />
                              <vs-select v-else-if="['현재위험가능성', '개선위험가능성'].indexOf(col) >= 0"
                                         class="w-full" autocomplete
                                         v-model="위험성평가표[col]"
                                          @change="(value) => {onChange위험도체크(value, 위험성평가표, col)}">
                                <vs-select-item v-for="(item, index) in optionsNumber1"
                                                :key="index" :value="item.value" :text="item.label" />
                              </vs-select>

                              <vs-select v-else-if="['현재위험중대성', '개선위험중대성'].indexOf(col) >= 0"
                                         class="w-full" autocomplete
                                         v-model="위험성평가표[col]"
                                         @change="(value) => {onChange위험도체크(value, 위험성평가표, col)}">
                                <vs-select-item v-for="(item, index) in optionsNumber2"
                                                :key="index" :value="item.value" :text="item.label"/>
                              </vs-select>
                              <ml-input v-else-if="['담당자'].indexOf(col) >= 0" v-model="위험성평가표[col]" class="w-full" />
                              <ml-textarea v-else-if="['세부작업명', '유해위험요인설명', '현재안전보건조치', '위험감소대책'].indexOf(col) >= 0"
                                           class="w-full m-0"
                                           v-model="위험성평가표[col]"
                              />
                              <p v-if="col === '현재위험도'">
                                {{ 위험성평가표['현재위험도'] }}<br>
                                ( {{ 위험성평가표['현재위험도수치'] }} )
                              </p>
                              <p v-else-if="col === '개선위험도'">
                                {{ 위험성평가표['개선위험도'] }}<br>
                                ( {{ 위험성평가표['개선위험도수치'] }} )
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>

                    </table>
                  </div>

                  <!-- 안전보건관리자_검토의견 -->
                  <div class="p-4 pr-0 pb-0">
                    <div class="pl-2 mb-2 flex justify-between items-center">
                      <h5 style="font-size: 14px;"><i class="dot" style="background: #2c2c2ccb"></i>{{ $t('안전·보건관리자 검토의견') }}</h5>
                    </div>
                    <table class="sub-table border-bottom" style="background: #fff;">
                      <colgroup>
                        <col width="300" />
                        <col width="" />
                        <col width="80" />
                        <col width="80" />
                        <col width="80" />
                        <col width="100" />
                        <col width="100" />
                      </colgroup>

                      <tr>
                        <th class="border-top" rowspan="2">{{ $t('세부작업명') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('검토의견') }}</th>
                        <th class="border-top" rowspan="1" colspan="3">{{ $t('검토의견 반영 위험도') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('검토자') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('비고') }}</th>
                      </tr>

                      <tr>
                        <th>{{ $t('가능성') }}</th>
                        <th>{{ $t('중대성') }}</th>
                        <th>{{ $t('위험도') }}</th>
                      </tr>

                      <tbody v-if="excel_i === editExcelIndex">
                        <tr v-for="(안전보건관리자_검토의견, index) in editExcelData.안전보건관리자_검토의견" :key="`안전보건관리자_검토의견_edit_${index}`">
                          <td v-for="col in ['세부작업명', '검토의견', '가능성', '중대성', '위험도', '검토자', '비고']" :key="`위험성평가정보_위험성평가_${col}`"
                              class="p-1">
                            <div class="p-1">
                              <div class="flex items-center justify-center gap-2">
                                <ml-textarea v-if="['세부작업명', '검토의견', '비고'].indexOf(col) >= 0"
                                             class="w-full m-0"
                                             v-model="안전보건관리자_검토의견[col]"
                                />
                                <ml-input v-else-if="['검토자'].indexOf(col) >= 0" v-model="안전보건관리자_검토의견[col]" class="w-full" />
                                <vs-select v-else-if="['가능성'].indexOf(col) >= 0"
                                           class="w-full" autocomplete
                                           v-model="안전보건관리자_검토의견[col]"
                                           @change="(value) => {onChange위험도체크(value, 안전보건관리자_검토의견, col)}">
                                  <vs-select-item v-for="(item, index) in optionsNumber1"
                                                  :key="index" :value="item.value" :text="item.label" />
                                </vs-select>

                                <vs-select v-else-if="['중대성'].indexOf(col) >= 0"
                                           class="w-full" autocomplete
                                           v-model="안전보건관리자_검토의견[col]"
                                           @change="(value) => {onChange위험도체크(value, 안전보건관리자_검토의견, col)}">
                                  <vs-select-item v-for="(item, index) in optionsNumber2"
                                                  :key="index" :value="item.value" :text="item.label"/>
                                </vs-select>
                                <p v-if="col === '위험도'">
                                  {{ 안전보건관리자_검토의견['위험도'] }}<br>
                                  ( {{ 안전보건관리자_검토의견['위험도수치'] }} )
                                </p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tbody>

                      <tbody v-else>
                        <tr v-for="(안전보건관리자_검토의견, index) in excel.안전보건관리자_검토의견" :key="`안전보건관리자_검토의견_${index}`">
                          <td v-for="col in ['세부작업명', '검토의견', '가능성', '중대성', '위험도', '검토자', '비고']" :key="`위험성평가정보_위험성평가_${col}`">
                            <div class="p-1">
                              <div class="flex items-center justify-center gap-2">
                                <p>{{ 안전보건관리자_검토의견[col] }}</p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- 조치 전후 사진 -->
                  <div class="p-4 pr-0 border-bottom">
                    <div class="pl-2 mb-2 flex justify-between items-center">
                      <h5 style="font-size: 14px;"><i class="dot" style="background: #2c2c2ccb"></i>{{ $t('조치 전/후 사진') }}</h5>
                    </div>
                    <table class="sub-table border-top border-bottom" style="background: #fff;">
                      <tr>
                        <th>{{ $t('조치 전 사진') }}</th>
                        <th>{{ $t('조치 후 사진') }}</th>
                      </tr>
                      <tr>
                        <td class="p-2" style="vertical-align: top;">
                          <div class="grid grid-cols-2 gap-2 items-start" style="min-height: 300px;">
                            <div v-for="(image, image_i) in excel.조치전후사진.전" :key="`조치전후사진_전_${image_i}`">
                              <img class="w-full" :src="'data:image/png;base64,' + image.base64" />
                            </div>
                          </div>
                        </td>
                        <td class="p-2" style="vertical-align: top;">
                          <div class="grid grid-cols-2 gap-2">
                            <div v-for="(image, image_i) in excel.조치전후사진.후" :key="`조치전후사진_전_${image_i}`">
                              <img class="w-full" :src="'data:image/png;base64,' + image.base64" />
                            </div>
                          </div>
                        </td>
                      </tr>
                    </table>
                  </div>


                </td>
              </tr>
              </tbody>

              <!-- 보기모드 -->
              <tbody v-else>
                <tr :class="{'errLine': errorDataIndex.indexOf(excel_i) >= 0}">
                  <td class="cursor-pointer" @click="excel.open = !excel.open">
                    <img src="@/assets/icons/triangle_down.svg"
                         class="mx-auto duration-200"
                         :style="{ transform: excel.open ? 'rotate(0deg)' : 'rotate(-90deg)' }"
                    />
                  </td>
                  <td>{{ excel_i + 1 }}</td>
                  <td v-for="col in ['사업장명', '공종명', '작업내용', '평가일', '실시기간_시작', '실시기간_종료']" :key="`위험성평가정보_위험성평가_${col}`">
                    <div class="p-1">
                      <div class="flex items-center justify-center gap-2">
                        <p v-if="['사업장명'].indexOf(col) >= 0">{{ excel[col].field_name }}</p>
                        <p v-else>{{ excel[col] }}</p>
                        <img v-if="excel.errorMsg[col]" src="@/assets/images/icon/icon-circle-alert-red.svg" />
                        <img v-else-if=" excel[col] && excel.errorMsg[col] === ''" src="@/assets/images/icon/icon-circle-success-green.svg" />
                      </div>
                      <p v-if="excel.errorMsg[col]" class="errMsg">{{ excel.errorMsg[col] }}</p>
                    </div>
                  </td>
                  <td>
                    <div class="flex justify-center gap-3">
                      <button @click="onClickEditExcelData(excel_i)">
                        <feather-icon icon="Edit2Icon" svgClasses="h-5 w-5" />
                      </button>
                      <button @click="onClickRemoveExcelData(excel_i)">
                        <feather-icon icon="Trash2Icon" svgClasses="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="excel.open">
                <td class="p-0 border-none" style="background: #fafafa;" colspan="9">
                  <!-- 위험성평가표 -->
                  <div class="p-4 pr-0 pb-0">
                    <div class="pl-2 mb-2 flex justify-between items-center ">
                      <h5 style="font-size: 14px;"><i class="dot" style="background: #2c2c2ccb"></i>{{ $t('위험성평가표') }}</h5>
                    </div>
                    <table class="sub-table border-bottom" style="background: #fff;">
                      <colgroup>
                        <col v-for="_ in 6" :key="`sub_col_1_${_}`" />
                        <col width="80" />
                        <col width="80" />
                        <col width="80" />
                        <col />
                        <col width="80" />
                        <col width="80" />
                        <col width="80" />
                        <col v-for="_ in 3" :key="`sub_col_3_${_}`" />
                      </colgroup>

                      <tr>
                        <th class="border-top" rowspan="2">{{ $t('세부작업명') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('평가구분') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('유해 위험요인') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('유해 위험요인 설명') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('재해형태') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('현재 안전보건조치') }}</th>
                        <th class="border-top" rowspan="1" colspan="3">{{ $t('현재 위험도') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('위험감소대책') }}</th>
                        <th class="border-top" rowspan="1" colspan="3">{{ $t('개선후 위험도') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('개선예정일') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('완료예정일') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('담당자') }}</th>
                      </tr>

                      <tr>
                        <th>{{ $t('가능성') }}</th>
                        <th>{{ $t('중대성') }}</th>
                        <th>{{ $t('위험도') }}</th>
                        <th>{{ $t('가능성') }}</th>
                        <th>{{ $t('중대성') }}</th>
                        <th>{{ $t('위험도') }}</th>
                      </tr>

                      <tr v-for="(위험성평가표, 위험성평가표_i) in excel.위험성평가표" :key="`위험성평가표_${위험성평가표_i}`">
                        <td v-for="col in [
                          '세부작업명', '평가구분', '유해위험요인', '유해위험요인설명', '재해형태', '현재안전보건조치',
                          '현재위험가능성', '현재위험중대성', '현재위험도', '위험감소대책', '개선위험가능성',
                          '개선위험중대성', '개선위험도', '개선예정일', '완료예정일', '담당자'
                          ]" :key="`위험성평가정보_위험성평가_${col}`">
                          <div class="p-1">
                            <div class="flex items-center justify-center gap-2">
                              <p v-if="col === '현재위험도'">
                                {{ 위험성평가표['현재위험도'] }}<br>
                                ( {{ 위험성평가표['현재위험도수치'] }} )
                              </p>
                              <p v-else-if="col === '개선위험도'">
                                {{ 위험성평가표['개선위험도'] }}<br>
                                ( {{ 위험성평가표['개선위험도수치'] }} )
                              </p>
                              <p v-else>{{ 위험성평가표[col] }}</p>
                              <img v-if="excel.errorMsg['위험성평가표'][위험성평가표_i][col]"
                                   src="@/assets/images/icon/icon-circle-alert-red.svg" />
                              <img v-else-if=" 위험성평가표[col] && excel.errorMsg['위험성평가표'][위험성평가표_i][col] === ''"
                                   src="@/assets/images/icon/icon-circle-success-green.svg" />
                            </div>
                            <p class="errMsg">{{ excel.errorMsg["위험성평가표"][위험성평가표_i][col] }}</p>
                          </div>
                        </td>
                      </tr>

                    </table>
                  </div>

                  <!-- 안전보건관리자_검토의견 -->
                  <div class="p-4 pr-0 pb-0">
                    <div class="pl-2 mb-2 flex justify-between items-center">
                      <h5 style="font-size: 14px;"><i class="dot" style="background: #2c2c2ccb"></i>{{ $t('안전·보건관리자 검토의견') }}</h5>
                    </div>
                    <table class="sub-table border-bottom" style="background: #fff;">
                      <colgroup>
                        <col width="300" />
                        <col width="" />
                        <col width="80" />
                        <col width="80" />
                        <col width="80" />
                        <col width="100" />
                        <col width="100" />
                      </colgroup>

                      <tr>
                        <th class="border-top" rowspan="2">{{ $t('세부작업명') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('검토의견') }}</th>
                        <th class="border-top" rowspan="1" colspan="3">{{ $t('검토의견 반영 위험도') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('검토자') }}</th>
                        <th class="border-top" rowspan="2">{{ $t('비고') }}</th>
                      </tr>

                      <tr>
                        <th>{{ $t('가능성') }}</th>
                        <th>{{ $t('중대성') }}</th>
                        <th>{{ $t('위험도') }}</th>
                      </tr>

                      <tr v-for="(안전보건관리자_검토의견, index) in excel.안전보건관리자_검토의견" :key="`안전보건관리자_검토의견_${index}`">
                        <td v-for="col in ['세부작업명', '검토의견', '가능성', '중대성', '위험도', '검토자', '비고']" :key="`위험성평가정보_위험성평가_${col}`">
                          <div class="p-1">
                            <div class="flex items-center justify-center gap-2">
                              <p>{{ 안전보건관리자_검토의견[col] }}</p>
                            </div>
                          </div>
                        </td>
                      </tr>

                    </table>
                  </div>

                  <!-- 조치 전후 사진 -->
                  <div class="p-4 pr-0 border-bottom">
                    <div class="pl-2 mb-2 flex justify-between items-center">
                      <h5 style="font-size: 14px;"><i class="dot" style="background: #2c2c2ccb"></i>{{ $t('조치 전/후 사진') }}</h5>
                    </div>
                    <table class="sub-table border-top border-bottom" style="background: #fff;">
                      <tr>
                        <th>{{ $t('조치 전 사진') }}</th>
                        <th>{{ $t('조치 후 사진') }}</th>
                      </tr>
                      <tr>
                        <td class="p-2" style="vertical-align: top;">
                          <div class="grid grid-cols-2 gap-2 items-start" style="min-height: 300px;">
                            <div v-for="(image, image_i) in excel.조치전후사진.전" :key="`조치전후사진_전_${image_i}`">
                              <img class="w-full" :src="'data:image/png;base64,' + image.base64" />
                            </div>
                          </div>
                        </td>
                        <td class="p-2" style="vertical-align: top;">
                          <div class="grid grid-cols-2 gap-2">
                            <div v-for="(image, image_i) in excel.조치전후사진.후" :key="`조치전후사진_전_${image_i}`">
                              <img class="w-full" :src="'data:image/png;base64,' + image.base64" />
                            </div>
                          </div>
                        </td>
                      </tr>
                    </table>
                  </div>


                </td>
              </tr>
              </tbody>
            </fragment>
          </table>
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import DateSelector from "@/components/selector/DateSelector";
import FieldSelector from "@/components/selector/FieldSelector";
import moment from "moment";
import HttpRequest from "@/util/HttpRequest";
import { RiskAssessmentBody } from "@/views/_BS/RiskAssessmentForm";
import config from "/public/config";
import ExcelJS from "exceljs";
import CheckTypeASelector from '@/components/selector/CheckTypeASelector'
import DisasterClassSelector from "@/components/selector/DisasterClassSelector.vue";

export default {
  components: {DisasterClassSelector, CheckTypeASelector, FieldSelector, FieldSelectModule, DateSelector },
  data() {
    return {
      optionsNumber1: [
        {value: 1, label: '1'},
        {value: 2, label: '2'},
        {value: 3, label: '3'},
        {value: 4, label: '4'},
        {value: 5, label: '5'}
      ],
      optionsNumber2: [
        {value: 1, label: '1'},
        {value: 2, label: '2'},
        {value: 3, label: '3'},
        {value: 4, label: '4'}
      ],
      fields: [],
      board: {
        projectTitle: this.$store.state.project.projectInfo.field_name,
        writer: {}, //작성자 정보
        readonly: false, // 읽기전용인가
        isPrint: false,
        viewSaveBtn: true,
        viewRemoveBtn: true,
      },

      form: {
        stdInfo: {},
        fields: [],
        company: null,
      },


      editExcelIndex: null,
      editExcelData: {},
      excelData: [],
      errorDataIndex: [],
    };
  },
  computed: {
    projectId() {
      return Number(this.$route.params.proj_id);
    },
    boardId() {
      return this.$route.params.boardId;
    },
    myInfo() {
      return this.$store.state.member.memberInfo;
    },
    excelLink() {
      return `${config.BASE_API_URL}/storage/app/media/%EC%A0%95%EA%B8%B0%EC%9C%84%ED%97%98%EC%84%B1%ED%8F%89%EA%B0%80_%ED%96%89%EC%A0%95%EA%B8%B0%EA%B4%80_rev.1.0.xlsx`;
    }
  },
  async mounted() {
    await this.init();
  },
  methods: {
    async init() {
      await this.$store.dispatch("USER_INFO");
      this.board.writer = {
        id: this.$store.state.auth.userInfo.id,
        name: this.$store.state.auth.userInfo.name,
        company_id: this.$store.state.auth.userInfo.company_id,
        department: this.$store.state.auth.userInfo.department,
        section: this.$store.state.auth.userInfo.section,
        rank: this.$store.state.auth.userInfo.rank,
        com_name: "",
      };

      await this.$store.dispatch("company/LOAD_COMPANY", {
        companyId: this.board.writer.company_id,
      });
      this.board.writer.com_name = this.$store.state.company.companyInfo.com_name;
      if (!this.board.writer.section) {
        this.board.writer.section = this.board.writer.com_name;
      }

      await this.loadFieldListAll();
    },
    async loadFieldListAll() {
      this.loading(true);
      const loadFieldList = (page) => {
        this.$store.dispatch("project/LOAD_FIELD_LIST", {
          projectId: this.projectId,
          page,
          perPage: 50,
        }).then((response) => {
          const data = response.data;
          this.fields.push(...data)
          if (response.last_page > page) {
            loadFieldList(page + 1);
          } else {
            this.loading(false);
          }
        });
      }
      await loadFieldList(1)
    },

    handleAppraisalType (type) {
      if (type === '기계(설비)적 요인') {
        return [
          {text: '끼임(감김)', value: '끼임(감김)'},
          {text: '위험한 표면(절단, 베임, 긁힘)', value: '위험한 표면(절단, 베임, 긁힘)'},
          {text: '기계(설비)의 낙하, 비래, 전복, 붕괴, 전도 위험 부분', value: '기계(설비)의 낙하, 비래, 전복, 붕괴, 전도 위험 부분'},
          {text: '충돌위험 부분', value: '충돌위험 부분'},
          {text: '넘어짐(미끄러짐, 걸림, 헛디딤)', value: '넘어짐(미끄러짐, 걸림, 헛디딤)'},
          {text: '추락위험 부분(개구부 등)', value: '추락위험 부분(개구부 등)'},
          {text: '기타', value: '기타'},
          {text: '직접입력', value: '직접입력'},
        ]
      } else if (type === '전기적 요인') {
        return [
          {text: '감전(안전전압초과)', value: '감전(안전전압초과)'},
          {text: '아크', value: '아크'},
          {text: '정전기', value: '정전기'},
          {text: '화재 / 폭발 위험', value: '화재 / 폭발 위험'},
          {text: '기타', value: '기타'},
          {text: '직접입력', value: '직접입력'},
        ]
      } else if (type === '화학(물질)적 요인') {
        return [
          {text: '가스', value: '가스'},
          {text: '증기', value: '증기'},
          {text: '에어로졸·흄', value: '에어로졸·흄'},
          {text: '액체·미스트', value: '액체·미스트'},
          {text: '고체(분진)', value: '고체(분진)'},
          {text: '반응성 물질', value: '반응성 물질'},
          {text: '방사선', value: '방사선'},
          {text: '화재 / 폭발 위험', value: '화재 / 폭발 위험'},
          {text: '복사열 / 폭발과압', value: '복사열 / 폭발과압'},
          {text: '기타', value: '기타'},
          {text: '직접입력', value: '직접입력'},
        ]
      } else if (type === '생물학적 요인') {
        return [
          {text: '병원성 미생물, 바이러스에 의한 감염', value: '병원성 미생물, 바이러스에 의한 감염'},
          {text: '유전자 변형물질(GMO)', value: '유전자 변형물질(GMO)'},
          {text: '알러지 및 미생물', value: '알러지 및 미생물'},
          {text: '동물', value: '동물'},
          {text: '식물', value: '식물'},
          {text: '기타', value: '기타'},
          {text: '직접입력', value: '직접입력'},
        ]
      } else if (type === '작업특성 요인') {
        return [
          {text: '소음', value: '소음'},
          {text: '초음파·초저주파음', value: '초음파·초저주파음'},
          {text: '진동', value: '진동'},
          {text: '근로자 실수(휴먼에러)', value: '근로자 실수(휴먼에러)'},
          {text: '저압 또는 고압상태', value: '저압 또는 고압상태'},
          {text: '질식위험·산소결핍', value: '질식위험·산소결핍'},
          {text: '중량물 취급작업', value: '중량물 취급작업'},
          {text: '반복작업', value: '반복작업'},
          {text: '불안정한 작업자세', value: '불안정한 작업자세'},
          {text: '작업(조작)도구', value: '작업(조작)도구'},
          {text: '기후 / 고온 / 한랭', value: '기후 / 고온 / 한랭'},
          {text: '기타', value: '기타'},
          {text: '직접입력', value: '직접입력'},
        ]
      } else if (type === '작업환경 요인') {
        return [
          {text: '기후 / 고온 / 한랭', value: '기후 / 고온 / 한랭'},
          {text: '조명', value: '조명'},
          {text: '공간 및 이동통로', value: '공간 및 이동통로'},
          {text: '주변 근로자', value: '주변 근로자'},
          {text: '작업시간', value: '작업시간'},
          {text: '조직 안전문화', value: '조직 안전문화'},
          {text: '화상', value: '화상'},
          {text: '작업(조작) 도구', value: '작업(조작) 도구'},
          {text: '기타', value: '기타'},
          {text: '직접입력', value: '직접입력'},
        ]
      }

    },
    onClickExcelUpload() {
      const excelInput = this.$refs.excelInput;
      excelInput.value = null;
      excelInput.click();
    },
    onSelectFile(event) {
      for (const file of event.target.files) {
        this.readFile(file);
      }
    },
    // 엑셀데이터 파싱
    async readFile (file) {
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(file);
      const media = workbook.model.media;

      for (const worksheet of workbook.worksheets) {
        const sections = ['위험성평가표', '안전보건관리자_검토의견', '조치전후사진'];
        let sectionCount = 0;
        const obj = {
          '위험성평가표': [],
          '안전보건관리자_검토의견': [],
          '조치전후사진': {
            '전': [],
            '후': [],
          }
        };
        const images = worksheet.getImages().map((image) => {
          try {
            return {
              rowIndex: image.range.tl.nativeRow + 1,
              columnIndex: image.range.tl.nativeCol + 1,
              buffer: media.find((m) => m.index === image.imageId).buffer,
              base64: new Buffer(media.find((m) => m.index === image.imageId).buffer).toString('base64'),
            }
          } catch (e) {
            return {
              rowIndex: -1,
              columnIndex: -1,
              base64: undefined,
            }
          }
        });

        obj["업체명"] = worksheet.getRow(1).getCell(2).value
        obj["사업장명"] = {
          field_id: -1,
          field_name: worksheet.getRow(2).getCell(2).value,
        }
        obj["평가자"] = worksheet.getRow(3).getCell(2).value
        obj["공종명"] = worksheet.getRow(4).getCell(2).value
        obj["작업내용"] = worksheet.getRow(5).getCell(2).value
        obj["평가일"] = worksheet.getRow(1).getCell(18).value
        obj["실시기간_시작"] = worksheet.getRow(2).getCell(18).value
        obj["실시기간_종료"] = worksheet.getRow(2).getCell(19).value

        for (let rowIndex = 8; rowIndex < worksheet.rowCount + 1; rowIndex++) { // 데이터는 1번째부터 시작하므로, 마지막 데이터까지 읽기 위해 rowCount + 1 해줌
          const row = worksheet.getRow(rowIndex);
          let _count = 0;
          for (let i = 0; i<row.values.length; i++) {
            if (row.values[i]) _count++;
          }
          if (_count === 0) {
            rowIndex += sectionCount === 0 ? 2 : sectionCount === 0 ? 1 : 0; // 검토의견 / 조치 전후 사진 Header 영역 스킵
            sectionCount += sectionCount < 2 ? 1 : 0;
            if (sectionCount < 2) {
              continue
            }
          }
          if (sectionCount === 0) {
            obj[sections[sectionCount]].push({
              '세부작업명' : row.getCell(2).value,
              '평가구분' : row.getCell(3).value,
              '유해위험요인': row.getCell(4).value,
              '유해위험요인설명': row.getCell(5).value,
              '재해형태': row.getCell(6).value,
              '현재안전보건조치': row.getCell(7).value,
              '현재위험가능성': row.getCell(8).value,
              '현재위험중대성': row.getCell(9).value,
              '현재위험도수치': row.getCell(10).value,
              '현재위험도': row.getCell(11).value,
              '위험감소대책': row.getCell(12).value,
              '개선위험가능성': row.getCell(13).value,
              '개선위험중대성': row.getCell(14).value,
              '개선위험도수치': row.getCell(15).value,
              '개선위험도': row.getCell(16).value,
              '개선예정일': row.getCell(17).value,
              '완료예정일': row.getCell(18).value,
              '담당자': row.getCell(19).value,
            })
          } else if (sectionCount === 1) {
            obj[sections[sectionCount]].push({
              '세부작업명' : row.getCell(1).value,
              '검토의견' : row.getCell(3).value,
              '가능성' : row.getCell(8).value,
              '중대성' : row.getCell(9).value,
              '위험도수치' : row.getCell(10).value,
              '위험도' : row.getCell(11).value,
              '검토자' : row.getCell(12).value,
              '비고' : row.getCell(13).value,
            })
          } else {
            // rowIndex
            const checkColIndex = 8 // Column H 이후부터는 조치후 사진
            const beforeImage = images.filter((image) => image.rowIndex === rowIndex && image.columnIndex < checkColIndex);
            const afterImage = images.filter((image) => image.rowIndex === rowIndex && image.columnIndex >= checkColIndex);

            if (beforeImage.length > 0 || afterImage.length > 0) {
              obj[sections[sectionCount]]['전'].push(...beforeImage)
              obj[sections[sectionCount]]['후'].push(...afterImage)
            }
          }
        }


        // 유효성 검사
        const errorMsg = {
          "사업장명": "",
          "평가일": "",
          "실시기간_시작": "",
          "실시기간_종료": "",
          "위험성평가표": [],
        }
        this.checkValue(obj, errorMsg);
        obj.errorMsg = errorMsg;
        this.excelData.push({...obj, open: false})
      }
    },
    checkValue(obj, errorMsg) {
      this.__checkValueField("사업장명", obj, errorMsg)
      this.__checkValueDate("평가일", obj, errorMsg)
      this.__checkValueDate("실시기간_시작", obj, errorMsg)
      this.__checkValueDate("실시기간_종료", obj, errorMsg)
      this.__checkValue위험성평가표("위험성평가표", obj, errorMsg)
    },
    __checkValueField(key, obj, errorMsg) {
      // 유효성 검사 : 사업장
      const findField = this.fields.find((field) => field.field_name === obj[key].field_name)
      if (findField) {
        obj[key].field_id = findField.id;
        obj[key].project_name = findField.project.field_name;
      } else {
        errorMsg[key] = "입력된 데이터가 검색되지 않습니다.";
      }
    },
    __checkValueDate(key, obj, errorMsg) {
      if (obj && obj[key]) {
        // 유효성 검사 : 날짜
        const isDate = (obj[key] === '') ? true : !isNaN(new Date(obj[key]))
        if (!isDate) {
          errorMsg[key] = "날짜 형식이 아닙니다.";
        } else {
          obj[key] = moment(obj[key]).format("YYYY-MM-DD");
        }
      }
    },
    __checkValue위험성평가표(key, obj, errorMsg) {
      if (obj && obj[key]) {
        for (let i = 0; i<obj[key].length; i++) {
          const sub = obj[key][i];
          if (!sub) continue;
          // 유효성 검사 : 날짜
          errorMsg[key][i] = {
            "개선예정일": "",
            "완료예정일": "",
          }
          this.__checkValueDate("개선예정일", sub, errorMsg[key][i])
          this.__checkValueDate("완료예정일", sub, errorMsg[key][i])
        }
      }
    },
    // 수정버튼이 눌렸을때
    onClickEditExcelData(index) {
      this.editExcelData = JSON.parse(JSON.stringify(this.excelData[index]));
      this.editExcelIndex = index;
    },
    // 수정을 완료했을때
    onClickFinishExcelData(excelData, excel_i) {
      const editExcelData = {...this.editExcelData}
      if (editExcelData["위험성평가표"]) {
        for (let i = 0; i<editExcelData["위험성평가표"].length; i++) {
          const sub = editExcelData["위험성평가표"][i];
          if (!sub) continue;
          // // 유효성 검사 : 날짜
          // editExcelData.errorMsg["위험성평가표"][i] = {
          //   "개선예정일": "",
          //   "완료예정일": "",
          // }
          this.__checkValueDate("개선예정일", sub["개선예정일"], editExcelData.errorMsg["위험성평가표"][i])
          this.__checkValueDate("완료예정일", sub["완료예정일"], editExcelData.errorMsg["위험성평가표"][i])
        }
      }

      // this.excelData[excel_i] = editExcelData;
      // 처럼 처리할 경우, 주소값이 변경되어 펼침/닫힘 되지 않은 문제가 있어서 아래와 같이 처리
      const keys = Object.keys(this.excelData[excel_i])
      for (const key of keys) {
        this.excelData[excel_i][key] = editExcelData[key];
      }
      this.onClickCancelExcelData();
    },
    onClickCancelExcelData() {
      this.editExcelData = {};
      this.editExcelIndex = null;
    },
    onClickRemoveExcelData(index) {
      this.excelData.splice(index, 1);
    },
    onSelectFieldSelector(value) {
      if (value.field_name) {
        this.$set(this.editExcelData, "사업장명", {
          field_id: value.id,
          field_name: value.field_name
        });
        this.$set(this.editExcelData.errorMsg, "사업장명", "");
      } else {
        this.$set(this.editExcelData, "사업장명", {
          field_id: 0,
          field_name: ""
        });
        this.$set(this.editExcelData.errorMsg, "사업장명", "입력된 데이터가 검색되지 않습니다.");
      }
    },
    async onClickSave() {
      // 엑셀 데이터가 없을때 에러
      if (this.excelData.length < 1) {
        this.customAlert("등록 대상 위험성평가 정보가 없습니다");
        return;
      }

      this.errorDataIndex = [];
      let check사업장 = false
      let excel = null;
      for (let i = 0; i < this.excelData.length; i++) {
        excel = this.excelData[i];

        if (excel.사업장명.field_id <= 0) {
          check사업장 = true
        }


        if (excel.errorMsg["사업장명"] || excel.errorMsg["평가일"] || excel.errorMsg["실시기간_시작"] || excel.errorMsg["실시기간_종료"]) {
          this.errorDataIndex.push(i);
        }
        if (excel.errorMsg["위험성평가표"].some((sub) => {
          return Object.values(sub).some((value) => value !== "")
        })) {
          this.errorDataIndex.push(i);
        }
      }

      if (this.errorDataIndex.length > 0) {
        if (check사업장) {
          this.customAlert("사업장명이 입력되지 않은 데이터가 있습니다");
        } else {
          this.customAlert("입력된 데이터가 유효하지 않습니다");
        }
        return;
      }

      const header = {
        full: moment().format("YYYY-MM-DD"),
        month: Number(moment().format("MM")),
        year: Number(moment().format("YYYY")),
        date: moment().format("YYYY-MM-DD"),
      };

      const posts = [];
      let ed = undefined;
      this.excelData = this.excelData.reverse()
      for (let excel_i = 0; excel_i < this.excelData.length; excel_i++) {
        this.loading(true);
        ed = this.excelData[excel_i];

        // stdInfo
        const stdInfo = {
          ...this.form.stdInfo,
          project_name: ed.사업장명.project_name,
          field_id: ed.사업장명.field_id,
          field_name: ed.사업장명.field_name,
        };


        // riskAppraisalList
        const riskAppraisalList = ed.위험성평가표.map((sub, sub_i) => {
          return {
            id: sub_i,
            input_check: false,
            input_1: sub.세부작업명,
            input_2: sub.평가구분,
            input_4: sub.유해위험요인,
            유해위험요인설명: sub.유해위험요인설명,
            input_3: sub.재해형태,
            input_5: sub.현재안전보건조치,
            input_6: sub.현재위험가능성,
            input_7: sub.현재위험중대성,
            input_8: this.checkRank(sub.현재위험도수치),
            input_8_color: this.checkColor(sub.현재위험도수치),
            input_8_value: sub.현재위험도수치,
            input_9: sub.위험감소대책,
            input_11: sub.개선위험가능성,
            input_12: sub.개선위험중대성,
            input_13: this.checkRank(sub.개선위험도수치),
            input_13_color: this.checkColor(sub.개선위험도수치),
            input_13_value: sub.개선위험도수치,
            input_14: sub.개선예정일,
            input_15: sub.완료예정일,
            input_16: sub.담당자,
          };
        });


        // riskAppraisalList
        const riskAppraisalList2 = ed.안전보건관리자_검토의견.map((sub) => {
          return {
            input1: sub.세부작업명,
            input2: sub.검토의견,
            input3: sub.가능성,
            input4: sub.중대성,
            input5: sub.위험도,
            input6: sub.검토자,
            input7: sub.비고,
          };
        });


        const dataURLtoFile = (dataurl, fileName) => {
          const arr = dataurl.split(',');
          const mime = arr[0].match(/:(.*?);/)[1];
          const bstr = atob(arr[1]);
          let n = bstr.length;
          const u8arr = new Uint8Array(n);

          while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
          }

          return new File([u8arr], fileName, {type:mime});
        }


        const photoList = []
        if (ed.조치전후사진 && ed.조치전후사진.전 && ed.조치전후사진.전.length > 0) {
          for (let i = 0; i<ed.조치전후사진.전.length; i++) {
            const file = dataURLtoFile(`data:image/png;base64,${ed.조치전후사진.전[i].base64}`, `boardId_${this.boardId}_조치전사진_${i}.png`)
            await this.$store.dispatch("file/FILE_UPLOAD", file).then((response) => {
              photoList.push({
                id: response.id,
                path: response.path,
                fileName: response.file_name,
                image: response,
              })
            });
          }
        }

        const photoListAfter = []
        if (ed.조치전후사진 && ed.조치전후사진.후 && ed.조치전후사진.후.length > 0) {
          for (let i = 0; i<ed.조치전후사진.후.length; i++) {
            const file = dataURLtoFile(`data:image/png;base64,${ed.조치전후사진.후[i].base64}`, `boardId_${this.boardId}_조치후사진_${i}.png`)
            await this.$store.dispatch("file/FILE_UPLOAD", file).then((response) => {
              photoListAfter.push({
                id: response.id,
                path: response.path,
                fileName: response.file_name,
                image: response,
              })
            });
          }
        }

        const disasters = ""
        const body = {
          ...JSON.parse(JSON.stringify(RiskAssessmentBody)),
          stdInfo,
          inputB_2: ed.공종명,
          inputB_3: ed.작업내용,
          inputB_4: ed.평가일,
          inputB_8: ed.실시기간_시작,
          inputB_9: ed.실시기간_종료,
          riskAppraisalList,
          riskAppraisalList2,
          photoList,
          photoListAfter,
          disasters,
        }

        const content = { header, body };
        posts.push({title: "정기 위험성평가", content: JSON.stringify(content), projectId: this.projectId});
        this.loading(false);
      }

      // post 전송
      this.loading(true);
      HttpRequest.put(`/api/v1/board/post/${this.boardId}/multiple`, {posts})
        .then(() => {
          // 초기화
          this.excelData = [];
          this.editExcelData = [];
          this.editExcelIndex = null;

          this.loading(false);
          this.customAlert("위험성 평가가 등록되었습니다", "위험성 평가 등록");
        })
        .catch(() => {
          this.loading(false);
          this.customAlert("서버에서 오류가 발생했습니다. 관리자에게 문의해주세요");
        });
    },
    checkColor(value) {
      if (value >= 1 && value <= 3) {
        return "#28C76F";
      } else if (value >= 4 && value <= 6) {
        return "#FF8510";
      } else {
        return "#E42728";
      }
    },
    checkRank(result) {
      if (result >= 1 && result <= 3) return "F";
      else if (result >= 4 && result <= 6) return "E";
      else if (result === 8) return "D";
      else if (result >= 9 && result <= 12) return "C";
      else if (result === 15) return "B";
      else if (result >= 16 && result <= 20) return "A";
    },
    customAlert(msg, title) {
      const customAlert = new CustomEvent("customAlert", {
        detail: {
          title: title ? title : "오류가 발생했습니다",
          message: msg,
        },
      });
      window.dispatchEvent(customAlert);
    },
    loading(open) {
      const loadingDialog = new CustomEvent("loadingDialog", {
        detail: {
          open,
        },
      });
      window.dispatchEvent(loadingDialog);
    },
    onChangeDate (value, 위험성평가표, key, errorMsg) {
      errorMsg[key] = '';
    },
    onChange위험도체크 (value, 위험성평가표) {
      const getResult = function (result) {
        if (result >= 1 && result <= 3) return 'F'
        else if (result >= 4 && result <= 6) return 'E'
        else if (result === 8) return 'D'
        else if (result >= 9 && result <= 12) return 'C'
        else if (result === 15) return 'B'
        else if (result >= 16 && result <= 20) return 'A'
      }

      const getResultRisk = function (data) {
        switch (data) {
        case 'A': return '매우높음'
        case 'B': return '높음'
        case 'C': return '약간높음'
        case 'D': return '보통'
        case 'E': return '낮음'
        case 'F': return '매우낮음'
        }
      }

      // 위험성평가표
      if (위험성평가표.현재위험가능성) {
        const 현재위험가능성 = Number(위험성평가표['현재위험가능성'])
        const 현재위험중대성 = Number(위험성평가표['현재위험중대성'])
        const 개선위험가능성 = Number(위험성평가표['개선위험가능성'])
        const 개선위험중대성 = Number(위험성평가표['개선위험중대성'])
        위험성평가표.현재위험도수치 = 현재위험가능성 * 현재위험중대성
        위험성평가표.현재위험도 = getResultRisk(getResult(위험성평가표.현재위험도수치))
        위험성평가표.개선위험도수치 = 개선위험가능성 * 개선위험중대성
        위험성평가표.개선위험도 = getResultRisk(getResult(위험성평가표.개선위험도수치))
      }

      // 조치후개선
      if (위험성평가표.가능성) {
        const 가능성 = Number(위험성평가표['가능성'])
        const 중대성 = Number(위험성평가표['중대성'])
        위험성평가표.위험도수치 = 가능성 * 중대성
        위험성평가표.위험도 = getResultRisk(getResult(위험성평가표.위험도수치))
      }
    },
  },
};
</script>

<style scoped lang="scss">
table {
  table-layout: fixed;
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    text-align: center;
    padding: 0;
  }

  th {
    font-weight: 700;
    font-size: 14px;
    line-height: 17px;

    background: #fafafa;
    border-top: 1px solid #cdcdcd;
    border-bottom: 1px solid #cdcdcd;
    padding: 16px 0;
  }

  td {
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;

    border-bottom: 1px solid #cdcdcd;
    padding: 16px 0;
  }

  .sub-table {
    border-left: 1px solid #cdcdcd;
    border-top: 0;
    border-bottom: 0;
    border-right: 0;

    th {
      background: #7e72f20d;
      padding: 16px 10px;
    }

    th, td {
      border-top: 0;
      border-left: 0;
      border-bottom: 0;
      border-right: 1px solid #cdcdcd;
    }

    tr {
      border-bottom: 1px solid #cdcdcd;

      &:last-child {
        border-bottom: 0;
      }
    }
  }

  button {
    width: 32px;
    height: 32px;

    display: flex;
    align-items: center;
    justify-content: center;

    color: #7e72f2;
    background: none;
    border: 1px solid #7e72f2;
    border-radius: 4px;
    cursor: pointer;

    span {
      margin: 0;
    }
  }

  .check {
    justify-content: center !important;

    ::v-deep {
      span {
        margin: 0;
      }
    }
  }
}

.errMsg {
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  text-align: center;
  color: #ea5455;
  margin-top: 2px;
}
.errLine {
  color: #ea5455
}
</style>
